import React, { useEffect, useRef, useState } from "react";
import { Formik, Field, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import { ActionSection, FieldWrappers, InputGroup, Label, ModalHeader, ScrollableContent, SideCancelBtn, SubmitBtn, Wrapper } from "../../Styles/InputStyles";
import { toast } from "react-toastify";

const WifiPasswordModal = ({
    isWifiPassword,
    setIsWifiPassword,
    handleDetailModalClose,
    wifiDetailsMutate,
    wifiDetailsLoading,
    selectedData,
    tenantId,
    wifiListData,
    wifiEditDetailsMutate,
    wifiEditDetailsLoading
}) => {
    const [height, setHeight] = useState(window.innerHeight);
    const formikRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Get initial values from wifiListData if available
    const getInitialValues = () => {
        const wifiCreds = wifiListData?.wifiCreds || [];

        // Find mobile and laptop entries
        const mobileEntry = wifiCreds.find(item => item.device === "mobile");
        const laptopEntry = wifiCreds.find(item => item.device === "laptop");

        return {
            devices: [
                {
                    deviceType: "mobile",
                    userName: mobileEntry?.title || "",
                    wifiPassword: mobileEntry?.password || "",
                    id: mobileEntry?.id || null,
                },
                {
                    deviceType: "laptop",
                    userName: laptopEntry?.title || "",
                    wifiPassword: laptopEntry?.password || "",
                    id: laptopEntry?.id || null,
                }
            ]
        };
    };

    const initialValues = getInitialValues();

    const validationSchema = Yup.object().shape({
        devices: Yup.array().of(
            Yup.object().shape({
                deviceType: Yup.string(),
                userName: Yup.string()
                    .test(
                        'conditional-username',
                        'Username is required when Wi-Fi password is provided',
                        function (value) {
                            const { wifiPassword } = this.parent;
                            if (wifiPassword && wifiPassword.trim().length > 0) {
                                return value && value.trim().length > 0;
                            }
                            if (value && value.trim().length > 0) {
                                return value.trim().length >= 3 && value.trim().length <= 30;
                            }
                            return true;
                        }
                    )
                    .min(3, "Username must be at least 3 characters long")
                    .max(30, "Username must not exceed 30 characters"),
                wifiPassword: Yup.string()
                    .test(
                        'conditional-wifi-password',
                        'Wi-Fi password is required when username is provided',
                        function (value) {
                            const { userName } = this.parent;
                            if (userName && userName.trim().length > 0) {
                                return value && value.trim().length > 0;
                            }
                            if (value && value.trim().length > 0) {
                                return value.trim().length >= 4 && value.trim().length <= 30;
                            }
                            return true;
                        }
                    )
                    .min(4, "Wi-Fi password must be at least 4 characters long")
                    .max(30, "Wi-Fi password must not exceed 30 characters"),
            })
        )
    });

    const handleSubmit = (values) => {
        const filteredDevices = values.devices.filter(device =>
            device.userName?.trim() || device.wifiPassword?.trim()
        );

        if (filteredDevices.length === 0) {
            toast.error("Please configure at least one device");
            return;
        }

        // Check if any device has an id (existing record)
        const hasExistingData = filteredDevices.some(device => device.id !== null && device.id !== undefined);

        if (hasExistingData) {
            // Edit API call with id, title, password, device
            const editPayload = {
                tenantId: tenantId,
                creds: filteredDevices.map(device => ({
                    id: device.id || null,
                    device: device.deviceType,
                    title: device.userName?.trim() || "",
                    password: device.wifiPassword?.trim() || "",
                }))
            };
            wifiEditDetailsMutate(editPayload);
        } else {
            // Add API call
            const addPayload = {
                tenantId: tenantId,
                creds: filteredDevices.map(device => ({
                    device: device.deviceType,
                    title: device.userName?.trim() || "",
                    password: device.wifiPassword?.trim() || "",
                }))
            };
            wifiDetailsMutate(addPayload);
        }
    };

    return (
        <Container style={{ height: `${height}px`, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
            <ModalHeader>
                <h1 className="text-lg font-medium text-white">Wi-Fi Credentials</h1>
                <button onClick={handleDetailModalClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <div className="custom-scrollbar flex h-full flex-col justify-between px-6">
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, errors, touched, setFieldError }) => {

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                    <Wrapper>
                                        <FieldArray name="devices">
                                            {({ remove, push, form }) => (
                                                <>
                                                    {/* Mobile Section */}
                                                    <DeviceSection>
                                                        <div>
                                                            <DeviceTitle>
                                                                Mobile Device

                                                            </DeviceTitle>
                                                            <p className="text-xs text-gray-500 mt-2">
                                                                Fill both fields to configure, or leave empty to skip
                                                            </p>
                                                        </div>
                                                        <InputGroup>
                                                            <Label>Wi-Fi Username / Network</Label>
                                                            <FieldWrappers>
                                                                <Field
                                                                    type="text"
                                                                    name="devices[0].userName"
                                                                    placeholder="Enter mobile username"
                                                                    className="w-full"
                                                                />
                                                            </FieldWrappers>
                                                            {errors.devices?.[0]?.userName && touched.devices?.[0]?.userName && (
                                                                <div className="mt-1 text-xs text-red-500">
                                                                    {errors.devices[0].userName}
                                                                </div>
                                                            )}
                                                        </InputGroup>

                                                        <InputGroup>
                                                            <Label>Wi-Fi Password</Label>
                                                            <FieldWrappers>
                                                                <Field
                                                                    type="text"
                                                                    name="devices[0].wifiPassword"
                                                                    placeholder="Enter mobile Wi-Fi password"
                                                                    className="w-full"
                                                                />
                                                            </FieldWrappers>
                                                            {errors.devices?.[0]?.wifiPassword && touched.devices?.[0]?.wifiPassword && (
                                                                <div className="mt-1 text-xs text-red-500">
                                                                    {errors.devices[0].wifiPassword}
                                                                </div>
                                                            )}
                                                        </InputGroup>
                                                    </DeviceSection>

                                                    {/* Laptop Section */}
                                                    <DeviceSection>
                                                        <div>
                                                            <DeviceTitle>
                                                                Laptop/Computer

                                                            </DeviceTitle>
                                                            <p className="text-xs text-gray-500 mt-2">
                                                                Fill both fields to configure, or leave empty to skip
                                                            </p>
                                                        </div>
                                                        <InputGroup>
                                                            <Label>Username</Label>
                                                            <FieldWrappers>
                                                                <Field
                                                                    type="text"
                                                                    name="devices[1].userName"
                                                                    placeholder="Enter laptop username"
                                                                    className="w-full"
                                                                />
                                                            </FieldWrappers>
                                                            {errors.devices?.[1]?.userName && touched.devices?.[1]?.userName && (
                                                                <div className="mt-1 text-xs text-red-500">
                                                                    {errors.devices[1].userName}
                                                                </div>
                                                            )}
                                                        </InputGroup>

                                                        <InputGroup>
                                                            <Label>Wi-Fi Password</Label>
                                                            <FieldWrappers>
                                                                <Field
                                                                    type="text"
                                                                    name="devices[1].wifiPassword"
                                                                    placeholder="Enter laptop Wi-Fi password"
                                                                    className="w-full"
                                                                />
                                                            </FieldWrappers>
                                                            {errors.devices?.[1]?.wifiPassword && touched.devices?.[1]?.wifiPassword && (
                                                                <div className="mt-1 text-xs text-red-500">
                                                                    {errors.devices[1].wifiPassword}
                                                                </div>
                                                            )}
                                                        </InputGroup>
                                                    </DeviceSection>
                                                </>
                                            )}
                                        </FieldArray>
                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn
                                        type="button"
                                        onClick={() => {
                                            handleDetailModalClose();
                                        }}
                                    >
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn
                                        type="submit"
                                        disabled={wifiDetailsLoading || wifiEditDetailsLoading}
                                    >
                                        {(wifiDetailsLoading || wifiEditDetailsLoading) ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const DeviceSection = tw.div`rounded-lg p-4 border border-gray-200 space-y-4`;
const DeviceTitle = tw.h2`text-base font-semibold text-gray-700 pb-2 border-b border-gray-200 flex items-center`;
const DeviceBadge = tw.span`bg-primary-color/10 text-primary-color text-xs font-medium px-2.5 py-0.5 rounded-full ml-2`;

export default WifiPasswordModal;